import { TransactionStatus } from '@back-office/ui';
import _ from 'lodash';
import { Instance } from 'mobx-state-tree';
import ms from 'ms';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useMount, useToggle, useUnmount } from 'react-use';
import {
  REFUND_TYPE,
  SORTED_REQUEST_TYPES,
  STATUS_CODE_NOT_AUTHORIZED,
  STATUS_CODE_SUCCESS,
  VOID_TYPE,
} from '../../constants';
import useInject from '../../hooks/useInject';
import RefundRequestTypesModel from '../../stores/request.store';
import TransactionDetail from '../../stores/transaction.store';

type RequestRefundType = {
  id: string;
  name: string;
  description: string;
  status: string;
};

type UseViewModelReturn = {
  voidDialog: boolean;
  toggleVoidDialog: (value: boolean) => void;
  requestRefundDialog: boolean;
  toggleRequestRefundDialog: (value: boolean) => void;
  isTxVoidable: boolean;
  isTxRefunded: boolean;
  isTxRefundPending: boolean;
  isTxRefundable: boolean;
  isVoidRefundPermission: boolean;
  statusLabel: string;
  handleVoid: () => Promise<void>;
  handleRequestRefund: (reasonType: string, reason: string) => Promise<void>;
  handleCancelRefund: () => void;
  onClickRequestRefund: () => Promise<void>;
  sortAndFilterRequestRefundTypes: (array: RequestRefundType[]) => string[];
  isAdmin: boolean;
  errorMessage: string;
  requestTypes: Instance<typeof RefundRequestTypesModel>;
  detail: Instance<typeof TransactionDetail>;
  getTransactionDetail: (txId: string) => void;
  alertMessage: string | undefined;
  setAlertMessage: (value: string) => void;
};

const useViewModel = (txId: string | null): UseViewModelReturn => {
  const {
    alert,
    auth,
    detail,
    requestTypes,
    getTransactionDetail,
    setLoading,
    voidTransaction,
    requestRefundTransaction,
    cancelRefundTransaction,
    getRequestTypes,
  } = useInject((root) => root);
  const history = useHistory();
  const [alertMessage, setAlertMessage] = useState<string>();

  const { t } = useTranslation();
  const [voidDialog, toggleVoidDialog] = useToggle(false);
  const [requestRefundDialog, toggleRequestRefundDialog] = useToggle(false);
  const isAdmin = useMemo(
    () => auth.currentUser.role === 'ADMIN',
    [auth.currentUser.role]
  );
  const [errorMessage, setErrorMessage] = useState<string>('');

  const latestRequestRefund = useMemo(
    () => _.last(detail.requestRefund),
    [detail.requestRefund]
  );

  const isTxVoidable = useMemo(() => {
    const isTxAuthorized = detail.status === TransactionStatus.Authorized;
    const isVoided = detail.child?.type === VOID_TYPE;
    return isTxAuthorized && !isVoided;
  }, [detail.child?.type, detail.status]);

  const isTxRefunded = useMemo(() => {
    const isRequestRefundApproved = latestRequestRefund?.status === 'approved';
    const isRefundedByAdmin = detail.child?.type === REFUND_TYPE;
    return isRequestRefundApproved || isRefundedByAdmin;
  }, [detail.child?.type, latestRequestRefund?.status]);

  const isTxRefundPending = useMemo(
    () => latestRequestRefund?.status === 'pending',
    [latestRequestRefund?.status]
  );

  const isTxRefundable = useMemo(
    () =>
      detail.status === TransactionStatus.Cleared &&
      !isTxRefundPending &&
      !isTxRefunded,
    [detail.status, isTxRefundPending, isTxRefunded]
  );

  const isVoidRefundPermission = useMemo(
    () => auth.currentUser.permissionId === 1,
    [auth.currentUser.permissionId]
  );
  const statusLabel = useMemo(() => {
    const [logs] = detail.auditTrail || [];
    return t(`transaction_list.status.${logs?.label}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail.auditTrail]);

  useMount(() => {
    if (txId) {
      getTransactionDetail(txId);
    } else {
      history.push('/');
    }
  });

  useEffect(() => {
    if (alertMessage) {
      const timeout = setInterval(() => {
        clearInterval(timeout);
        setAlertMessage(undefined);
      }, ms('5s'));
    }
  }, [alertMessage]);

  useUnmount(() => {
    detail.reset();
  });

  const handleVoid = async (): Promise<void> => {
    if (txId) {
      setLoading(true);
      try {
        const response = await voidTransaction(txId);
        const { status } = response;
        if (status.code === STATUS_CODE_SUCCESS) {
          setAlertMessage(t('dialog.success_voided'));
          await getTransactionDetail(txId);
        }
        setLoading(false);
      } catch (error) {
        if (error !== STATUS_CODE_NOT_AUTHORIZED) {
          await getTransactionDetail(txId);
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const handleRequestRefund = async (
    reasonType: string,
    reason: string
  ): Promise<void> => {
    if (txId) {
      setLoading(true);
      try {
        const response = await requestRefundTransaction(
          txId,
          reason,
          reasonType
        );
        const { status } = response;
        if (status.code === STATUS_CODE_SUCCESS) {
          setAlertMessage(t('request_refund.request_refund_alert'));
          await getTransactionDetail(txId);
        }
      } catch (error) {
        if (error !== STATUS_CODE_NOT_AUTHORIZED) {
          await getTransactionDetail(txId);
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCancelRefund = (): void => {
    if (!txId || !latestRequestRefund?.id) return;
    alert.openAlert({
      cancelBtn: 'dialog.cancel',
      message: 'request_refund.cancel_refund_dialog_message',
      okBtn: 'dialog.confirm',
      title: 'request_refund.cancel_refund_dialog_title',
      async handleOK() {
        setLoading(true);
        alert.closeDialog();
        try {
          const response = await cancelRefundTransaction(
            latestRequestRefund.id,
            txId
          );
          const { status } = response;
          if (status.code === STATUS_CODE_SUCCESS) {
            setAlertMessage(t('request_refund.cancel_refund_alert'));
            await getTransactionDetail(txId);
          }
        } catch (error) {
          if (error !== STATUS_CODE_NOT_AUTHORIZED) {
            await getTransactionDetail(txId);
          }
        } finally {
          setLoading(false);
        }
      },
      handleCancel() {
        alert.closeDialog();
      },
    });
  };

  const onClickRequestRefund = async (): Promise<void> => {
    await getRequestTypes();
    setErrorMessage('');
    toggleRequestRefundDialog(true);
  };

  const sortAndFilterRequestRefundTypes = (
    array: {
      id: string;
      name: string;
      description: string;
      status: string;
    }[]
  ): string[] => {
    const requestTypesFilter: string[] = array.reduce(
      (requestType: string[], obj) => {
        if (obj.status === 'ACTIVE') requestType.push(obj.name);

        return requestType;
      },
      []
    );

    return [
      ...SORTED_REQUEST_TYPES.filter((requestType) =>
        requestTypesFilter.includes(requestType)
      ),
      ...requestTypesFilter.filter(
        (requestType) => !SORTED_REQUEST_TYPES.includes(requestType)
      ),
    ];
  };

  return {
    voidDialog,
    toggleVoidDialog,
    requestRefundDialog,
    toggleRequestRefundDialog,
    isTxVoidable,
    isTxRefunded,
    isTxRefundPending,
    isTxRefundable,
    isVoidRefundPermission,
    statusLabel,
    handleVoid,
    handleRequestRefund,
    handleCancelRefund,
    onClickRequestRefund,
    sortAndFilterRequestRefundTypes,
    errorMessage,
    isAdmin,
    requestTypes,
    detail,
    getTransactionDetail,
    alertMessage,
    setAlertMessage,
  };
};

export default useViewModel;
